import { db } from './localDB';
import {
  Chore,
  Activity,
  Household,
  HouseholdAggregate,
  UUID,
  ChoreAggregate,
  Schedule,
  assignmentsWithoutMember,
  withoutMember,
  withChild,
} from '../domain';
import { v7 as uuidv7 } from 'uuid';

export class HouseholdService {
async getHouseholdAggregates(userId: string): Promise<HouseholdAggregate[]> {
  // 1. Fetch Households
  const households = await db.households
    .filter(h => h.member_ids.includes(userId))
    .toArray();
  const householdIds = households.map(h => h.id);

  // 2. Fetch all Chores for these households
  const chores = await db.chores
    .where('household_id')
    .anyOf(householdIds)
    .toArray();
  const choreIds = chores.map(c => c.id);

  // 3. Fetch all Activities for these chores
  const activities = await db.activities
    .where('chore_id')
    .anyOf(choreIds)
    .reverse() // Get newest first
    .limit(2000) // Safety cap for global view
    .toArray();

  // 4. Group Activities by Chore ID
  const activitiesByChore = activities.reduce((acc, act) => {
    acc[act.chore_id] = acc[act.chore_id] || [];
    acc[act.chore_id].push(act);
    return acc;
  }, {} as Record<string, Activity[]>);

  // 5. Group ChoreAggregates by Household ID
  const choresByHousehold = chores.reduce((acc, chore) => {
    const choreAgg: ChoreAggregate = {
      chore,
      household: households.find(h => h.id === chore.household_id)!,
      activities: activitiesByChore[chore.id] || []
    };
    acc[chore.household_id] = acc[chore.household_id] || [];
    acc[chore.household_id].push(choreAgg);
    return acc;
  }, {} as Record<string, ChoreAggregate[]>);

  // 6. Final Assembly
  return households.map(household => ({
    household: household,
    members: household.member_ids.map((id, idx) => ({
      id,
      name: household.member_display_names[idx]
    })) || [],
    chores: choresByHousehold[household.id] || []
  }));
}

  async createHousehold(name: string, ownerId: UUID, ownerDisplayName: string): Promise<Household> {
    const household: Household = {
      id: uuidv7(),
      name,
      created_at: new Date().toISOString(),
      owner_id: ownerId,
      owner_display_name: ownerDisplayName,
      member_ids: [ownerId], // Owner is automatically a member
      member_display_names: [ownerDisplayName],
    };

    await db.households.add(household);

    return household;
  }

  async deleteHousehold(householdId: UUID): Promise<void> {
    await db.transaction('rw', [db.households, db.chores, db.activities], async () => {
      const choreIds = await db.chores.where('household_id').equals(householdId).primaryKeys();
      await db.chores.where('household_id').equals(householdId).delete();
      if (choreIds.length > 0) {
        await db.activities.where('chore_id').anyOf(choreIds).delete();
      }
      await db.households.delete(householdId);
    });
  }

  /** Applies here what household-removeMember did on the server, so the owner sees it at once. */
  async removeMember(householdId: UUID, memberId: UUID): Promise<void> {
    await db.transaction('rw', [db.households, db.chores], async () => {
      const household = await db.households.get(householdId);
      if (!household) return;
      await db.households.update(householdId, withoutMember(household, memberId));
      const chores = await db.chores.where('household_id').equals(householdId).toArray();
      for (const chore of chores) {
        const assignedTo = assignmentsWithoutMember(chore.assigned_to ?? [], memberId);
        if (assignedTo) await db.chores.update(chore.id, { assigned_to: assignedTo });
      }
    });
  }

  /** Applies here what household-setChild did on the server, so the owner sees it at once. */
  async setChild(householdId: UUID, memberId: UUID, isChild: boolean): Promise<void> {
    await db.transaction('rw', db.households, async () => {
      const household = await db.households.get(householdId);
      if (!household) return;
      const childIds = withChild(household.child_ids, memberId, isChild);
      if (childIds) await db.households.update(householdId, { child_ids: childIds });
    });
  }

  async createChore(
    name: string,
    description: string,
    schedule: Schedule,
    householdId: UUID,
    assignments: { userId: UUID; isResponsible: boolean }[],
    labels?: string[]
  ): Promise<Chore> {
    const chore: Chore = {
      id: uuidv7(),
      name,
      description,
      labels: labels ?? [],
      schedule,
      household_id: householdId,
      created_at: new Date().toISOString(),
      assigned_to: assignments.map(assignment => ({
        userId: assignment.userId,
        isResponsible: assignment.isResponsible,
      })),
    };

    await db.chores.add(chore);

    return chore;
  }

  async markChoreDone(choreId: UUID, userId: UUID, completedAt: string = new Date().toISOString()): Promise<Activity> {
    const activity: Activity = {
      id: uuidv7(),
      chore_id: choreId,
      completed_at: completedAt,
      by_user: userId,
    };
    await db.transaction('rw', [db.activities, db.chores], async () => {
      await db.activities.add(activity);
      const chore = await db.chores.get(choreId);
      // A backdated completion may be older than the chore's most recent one;
      // only advance the denormalized cache when this completion is newer.
      if (!chore?.last_completed_at || new Date(completedAt) > new Date(chore.last_completed_at)) {
        await db.chores.update(choreId, {
          last_completed_at: completedAt,
          last_completed_by: userId,
        });
      }
    });
    return activity;
  }

  // Reverts a markChoreDone that a caller has confirmed hasn't synced yet. Recomputes
  // last_completed_at/last_completed_by from whatever activities remain (rather than trusting a
  // caller-supplied "previous value") so it stays correct even if other completions were logged
  // for this chore in between, including backdated ones that never advanced the cache.
  async undoMarkChoreDone(choreId: UUID, activityId: UUID): Promise<Chore | undefined> {
    return db.transaction('rw', [db.activities, db.chores], async () => {
      await db.activities.delete(activityId);
      const remaining = await db.activities.where('chore_id').equals(choreId).toArray();
      const latest = remaining.reduce<Activity | null>(
        (max, a) => (!max || new Date(a.completed_at) > new Date(max.completed_at) ? a : max),
        null
      );

      const chore = await db.chores.get(choreId);
      if (!chore) return undefined;

      const updated: Chore = { ...chore, last_completed_at: latest?.completed_at, last_completed_by: latest?.by_user };
      if (!latest) {
        delete updated.last_completed_at;
        delete updated.last_completed_by;
      }
      await db.chores.put(updated);
      return updated;
    });
  }



  async updateChore(
    choreId: UUID,
    name: string,
    description: string,
    schedule: Schedule,
    assignments: { userId: UUID; isResponsible: boolean }[],
    labels?: string[]
  ): Promise<Chore> {
    const choreUpdate = {
      name,
      description,
      labels: labels ?? [],
      schedule,
      assigned_to: assignments.map(assignment => ({
        userId: assignment.userId,
        isResponsible: assignment.isResponsible
      }))
    };

    await db.chores.update(choreId, choreUpdate);

    // Return the updated chore
    const updatedChore = await db.chores.get(choreId);
    return updatedChore!;
  }

  async deleteChore(choreId: UUID): Promise<void> {
    await db.transaction('rw', [db.chores, db.activities], async () => {
      await db.activities.where('chore_id').equals(choreId).delete();
      await db.chores.delete(choreId);
    });
  }
}

export const householdService = new HouseholdService();